export const CONSENT_COOKIE = 'ct_consent';
export const CONSENT_STORAGE_KEY = 'cargotetris.consent.v1';
export const CONSENT_CHANGED = 'ct_consent_change';
// Async-загрузчик и основной bundle разделяют временный выбор при закрытом storage.
const MEMORY_KEY = Symbol.for('cargotetris.consent');
const SHARED_COOKIE_DOMAINS = {
  'cargotetris.com': 'cargotetris.com',
  'www.cargotetris.com': 'cargotetris.com',
  'app.cargotetris.com': 'cargotetris.com'
};

// Принимает только наш формат выбора; повреждённая запись не выдаёт согласие.
const parseConsent = (raw) => {
  try {
    if (typeof raw !== 'string' || raw.length > 2048) return null;
    const record = JSON.parse(raw);
    if (record?.version !== 1 || !['accepted', 'rejected'].includes(record.choice) ||
      !['SITE', 'APP'].includes(record.source)) return null;
    const consent = { version: 1, choice: record.choice, source: record.source };
    if (Number.isSafeInteger(record.updatedAt) && record.updatedAt > 0) consent.updatedAt = record.updatedAt;
    return consent;
  } catch { return null; }
};

const newestConsent = (records) => records.filter(Boolean).reduce((latest, candidate) =>
  !latest || (candidate.updatedAt || 0) > (latest.updatedAt || 0) ? candidate : latest, null);

const readStoredConsent = (browser) => {
  let transferred = null;
  try {
    const cookie = browser.document.cookie.split(';').map((part) => part.trim())
      .find((part) => part.startsWith(`${CONSENT_COOKIE}=`));
    if (cookie) transferred = parseConsent(decodeURIComponent(cookie.slice(CONSENT_COOKIE.length + 1)));
  } catch { /* Cookie могут быть недоступны или повреждены. */ }
  let saved = null;
  let persistenceFailed = false;
  try { saved = parseConsent(browser.localStorage.getItem(CONSENT_STORAGE_KEY)); }
  catch { persistenceFailed = true; }
  return { record: newestConsent([transferred, saved]), saved, persistenceFailed };
};

// Перезагрузка допустима только если она не потеряет новый выбор из памяти.
export const hasStoredAnalyticsConsent = (choice, browser = globalThis.window) =>
  Boolean(browser && readStoredConsent(browser).record?.choice === choice);

// Cookie передаёт выбор между поддоменами; после её исчезновения остаётся localStorage.
export const getAnalyticsConsent = (browser = globalThis.window) => {
  if (!browser) return 'unknown';
  const stored = readStoredConsent(browser);
  let persistenceFailed = stored.persistenceFailed;
  const memory = browser[MEMORY_KEY];
  // Старую cookie нельзя считать новым согласием, если её перезапись заблокирована.
  const record = newestConsent([stored.record, (persistenceFailed || memory?.persistenceFailed) && memory?.record]);
  if (!record) return 'unknown';
  if (JSON.stringify(stored.saved) !== JSON.stringify(record)) {
    try { browser.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record)); }
    catch { persistenceFailed = true; }
  }
  browser[MEMORY_KEY] = { record, persistenceFailed };
  return record.choice;
};

// Сохраняет выбор локально и передаёт его сессионной cookie без Max-Age/Expires.
export const setAnalyticsConsent = (choice, {
  browser = globalThis.window, source = 'SITE', cookieDomain = ''
} = {}) => {
  if (!browser || !['accepted', 'rejected'].includes(choice) || !['SITE', 'APP'].includes(source)) return;
  getAnalyticsConsent(browser);
  const updatedAt = Math.max(Date.now(), (browser[MEMORY_KEY]?.record.updatedAt || 0) + 1);
  const record = { version: 1, choice, source, updatedAt };
  const serialized = JSON.stringify(record);
  let persistenceFailed = false;
  try { browser.localStorage.setItem(CONSENT_STORAGE_KEY, serialized); }
  catch { persistenceFailed = true; }
  browser[MEMORY_KEY] = { record, persistenceFailed };
  const hostname = browser.location.hostname;
  const domain = cookieDomain.replace(/^\./, '') || SHARED_COOKIE_DOMAINS[hostname] || '';
  const shared = domain && (hostname === domain || hostname.endsWith(`.${domain}`)) ? `; Domain=${domain}` : '';
  const secure = browser.location.protocol === 'https:' ? '; Secure' : '';
  try { browser.document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(serialized)}; Path=/; SameSite=Lax${secure}${shared}`; }
  catch { /* localStorage или память сохраняют явный выбор при блокировке cookie. */ }
};

export const googleConsent = (accepted) => ({
  analytics_storage: accepted ? 'granted' : 'denied',
  ad_storage: accepted ? 'granted' : 'denied',
  ad_user_data: accepted ? 'granted' : 'denied',
  ad_personalization: 'denied'
});
